import { useQuery } from "@tanstack/react-query";
import {
	ArrowRight,
	BarChart3,
	LoaderCircle,
	LogOut,
	Package,
	RefreshCw,
	ShieldCheck,
	TriangleAlert,
} from "lucide-react";
import { useState } from "react";

import { authClient } from "@/auth-client";
import { api, fetchCurrentOperator } from "@/client";
import type { SugarReceivable } from "@/client";

function Loading() {
	return (
		<div className="grid min-h-screen place-items-center bg-[#f3f4ef] text-sm text-stone-500">
			<div className="flex items-center gap-2">
				<LoaderCircle className="size-4 animate-spin" />
				Confirmando acesso…
			</div>
		</div>
	);
}

function SignIn() {
	const [signingIn, setSigningIn] = useState(false);
	const denied = new URLSearchParams(window.location.search).has("error");

	return (
		<div className="relative grid min-h-screen place-items-center overflow-hidden bg-[#14251d] px-5 py-10">
			<div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_15%_15%,#5e9a72_0,transparent_28%),radial-gradient(circle_at_85%_75%,#c49858_0,transparent_32%)]" />
			<div className="relative w-full max-w-md rounded-[2rem] border border-white/10 bg-[#fbfbf8] p-7 shadow-2xl shadow-black/30 sm:p-9">
				<div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-950">
					<span className="grid size-11 place-items-center rounded-2xl bg-emerald-950 text-white">
						<BarChart3 className="size-5" />
					</span>
					SINDI Dashboard
				</div>
				<h1 className="mt-10 text-4xl font-semibold tracking-[-0.055em] text-stone-950">
					Os números certos, no mesmo lugar.
				</h1>
				<p className="mt-4 text-sm leading-6 text-stone-600">
					Entre com uma conta Google autorizada para acompanhar o painel de controle da SINDI.
				</p>
				{denied && (
					<p className="mt-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
						Esta conta ainda não está autorizada para o Dashboard.
					</p>
				)}
				<button
					type="button"
					disabled={signingIn}
					onClick={async () => {
						setSigningIn(true);
						await authClient.signIn.social({
							provider: "google",
							callbackURL: "/",
							errorCallbackURL: "/?error=access",
						});
					}}
					className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:opacity-60"
				>
					{signingIn ? (
						<LoaderCircle className="size-4 animate-spin" />
					) : (
						<ArrowRight className="size-4" />
					)}
					Entrar com Google
				</button>
			</div>
		</div>
	);
}

const eq50Formatter = new Intl.NumberFormat("pt-BR", {
	minimumFractionDigits: 0,
	maximumFractionDigits: 2,
});

const dateTimeFormatter = new Intl.DateTimeFormat("pt-BR", {
	timeZone: "America/Sao_Paulo",
	day: "2-digit",
	month: "short",
	hour: "2-digit",
	minute: "2-digit",
});

function eq50kg(quantityGrams: number) {
	return eq50Formatter.format(quantityGrams / 50_000);
}

function BasketCard(input: {
	basket: "BIGBAG" | "SACK_50KG";
	composition: SugarReceivable["composition"];
}) {
	const rows = input.composition
		.filter((row) => row.basket === input.basket)
		.toSorted((left, right) => right.quantityGrams - left.quantityGrams);
	const total = rows.reduce((sum, row) => sum + row.quantityGrams, 0);
	const largest = rows[0]?.quantityGrams ?? 1;
	const featuredRows = rows.slice(0, 3);
	const remainingRows = rows.slice(3);
	const bigBag = input.basket === "BIGBAG";

	return (
		<div className="rounded-[1.6rem] border border-stone-200/80 bg-[#fafaf7] p-5 sm:p-6">
			<div className="flex items-start justify-between gap-4">
				<div>
					<p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
						{bigBag ? "BigBag" : "Saco"}
					</p>
					<p className="mt-2 text-3xl font-semibold tracking-[-0.045em] text-stone-950">
						{eq50kg(total)}
					</p>
					<p className="mt-1 text-xs text-stone-500">eq50kg</p>
				</div>
				<span
					className={`grid size-11 place-items-center rounded-2xl ${
						bigBag ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"
					}`}
				>
					<Package className="size-5" />
				</span>
			</div>

			<div className="mt-7 space-y-4">
				{featuredRows.map((row) => (
					<div key={`${row.basket}:${row.origin}`}>
						<div className="flex items-baseline justify-between gap-4">
							<p className="truncate text-sm font-medium text-stone-700">{row.origin}</p>
							<p className="shrink-0 text-sm font-semibold tabular-nums text-stone-950">
								{eq50kg(row.quantityGrams)}
							</p>
						</div>
						<div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-200">
							<div
								className={`h-full rounded-full ${bigBag ? "bg-emerald-700" : "bg-amber-600"}`}
								style={{ width: `${(row.quantityGrams / largest) * 100}%` }}
							/>
						</div>
					</div>
				))}
				{remainingRows.length > 0 && (
					<div className="border-t border-stone-200 pt-4">
						<p className="mb-3 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-stone-400">
							Demais origens
						</p>
						<div className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
							{remainingRows.map((row) => (
								<div
									key={`${row.basket}:${row.origin}`}
									className="flex items-baseline justify-between gap-3 text-xs"
								>
									<p className="truncate text-stone-600">{row.origin}</p>
									<p className="shrink-0 font-semibold tabular-nums text-stone-800">
										{eq50kg(row.quantityGrams)}
									</p>
								</div>
							))}
						</div>
					</div>
				)}
				{rows.length === 0 && <p className="text-sm text-stone-500">Sem saldo neste cesto.</p>}
			</div>
		</div>
	);
}

function SugarReceivableCard(input: { metric: SugarReceivable }) {
	return (
		<section className="mt-8 rounded-[2rem] border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
			<div className="flex flex-col justify-between gap-6 border-b border-stone-200 pb-7 sm:flex-row sm:items-end">
				<div>
					<p className="text-xs font-semibold uppercase tracking-[0.17em] text-emerald-800">
						Total em eq50kg
					</p>
					<div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-1">
						<p className="text-5xl font-semibold tracking-[-0.06em] text-stone-950 sm:text-7xl">
							{eq50kg(input.metric.quantityGrams)}
						</p>
						<p className="pb-1 text-sm font-semibold text-stone-500 sm:pb-2">eq50kg</p>
					</div>
				</div>
				<div className="text-left text-xs leading-5 text-stone-500 sm:text-right">
					<p>Leitura {dateTimeFormatter.format(new Date(input.metric.asOf))}</p>
					<p>Fonte atualizada {dateTimeFormatter.format(new Date(input.metric.sourceUpdatedAt))}</p>
				</div>
			</div>

			<div className="mt-6 grid gap-4 lg:grid-cols-2">
				<BasketCard basket="BIGBAG" composition={input.metric.composition} />
				<BasketCard basket="SACK_50KG" composition={input.metric.composition} />
			</div>
		</section>
	);
}

export function App() {
	const session = authClient.useSession();
	const operator = useQuery({
		queryKey: ["current-operator", session.data?.user.id],
		queryFn: fetchCurrentOperator,
		enabled: !!session.data,
		retry: false,
	});
	const sugarReceivable = useQuery({
		queryKey: ["sugar-receivable"],
		queryFn: () => api.metrics.sugarReceivable(),
		enabled: !!operator.data,
		staleTime: 60_000,
	});

	if (session.isPending || (!!session.data && operator.isPending)) {
		return <Loading />;
	}
	if (!session.data) {
		return <SignIn />;
	}
	if (operator.error) {
		return (
			<div className="grid min-h-screen place-items-center bg-[#17271f] px-5 py-10">
				<div className="w-full max-w-md rounded-[2rem] bg-white p-8 shadow-2xl">
					<ShieldCheck className="size-7 text-red-700" />
					<h1 className="mt-6 text-3xl font-semibold tracking-[-0.05em] text-stone-950">
						Acesso indisponível
					</h1>
					<p className="mt-3 text-sm leading-6 text-stone-600">{operator.error.message}</p>
					<button
						type="button"
						onClick={() => authClient.signOut()}
						className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-950 px-4 py-3 text-sm font-semibold text-white"
					>
						<LogOut className="size-4" />
						Sair
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-[#f3f4ef]">
			<header className="border-b border-stone-200/80 bg-[#fafaf7]">
				<div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
					<div className="flex items-center gap-3">
						<span className="grid size-10 place-items-center rounded-xl bg-emerald-950 text-white">
							<BarChart3 className="size-5" />
						</span>
						<div>
							<p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-emerald-800">
								SINDI
							</p>
							<p className="text-sm font-semibold text-stone-900">Dashboard</p>
						</div>
					</div>
					<button
						type="button"
						onClick={() => authClient.signOut()}
						className="flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs font-semibold text-stone-600 transition hover:border-stone-300 hover:text-stone-950"
					>
						<LogOut className="size-3.5" />
						Sair
					</button>
				</div>
			</header>

			<main className="mx-auto max-w-7xl px-5 py-9 sm:px-8 sm:py-12">
				<div className="max-w-3xl">
					<p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800">
						Visão geral
					</p>
					<h1 className="mt-3 text-4xl font-semibold tracking-[-0.055em] text-stone-950 sm:text-5xl">
						Açúcar ainda a receber
					</h1>
					<p className="mt-4 max-w-2xl text-sm leading-6 text-stone-600 sm:text-base">
						Saldo das OCs ativas por apresentação e origem contratada.
					</p>
				</div>

				{sugarReceivable.isPending && (
					<section className="mt-8 grid min-h-80 place-items-center rounded-[2rem] border border-stone-200 bg-white shadow-sm">
						<div className="flex items-center gap-2 text-sm text-stone-500">
							<LoaderCircle className="size-4 animate-spin" />
							Atualizando o saldo…
						</div>
					</section>
				)}

				{sugarReceivable.error && (
					<section className="mt-8 rounded-[2rem] border border-red-100 bg-white p-8 shadow-sm">
						<TriangleAlert className="size-6 text-red-700" />
						<h2 className="mt-5 text-2xl font-semibold tracking-[-0.04em] text-stone-950">
							Saldo temporariamente indisponível
						</h2>
						<p className="mt-2 max-w-xl text-sm leading-6 text-stone-600">
							O painel não recebeu uma leitura logística completa e não exibirá um total parcial.
						</p>
						<button
							type="button"
							onClick={() => sugarReceivable.refetch()}
							className="mt-6 flex items-center gap-2 rounded-xl border border-stone-200 bg-stone-50 px-4 py-2.5 text-sm font-semibold text-stone-700 transition hover:border-stone-300"
						>
							<RefreshCw className="size-4" />
							Tentar novamente
						</button>
					</section>
				)}

				{sugarReceivable.data && <SugarReceivableCard metric={sugarReceivable.data} />}
			</main>
		</div>
	);
}
